// Central-plane API types and fetch functions (consumed by @marque/app-central).

export class ApiError extends Error {
    constructor(
        readonly status: number,
        message: string,
        readonly fieldErrors: Record<string, string[]> = {},
    ) {
        super(message)
    }
}

export interface OperatorSession {
    operatorId: number
    displayName: string
    env: string
    permissions: string[]
}

export interface TenantSummary {
    tenantId: string
    slug: string
    displayName: string
    provisioningMode: string
    status: string
    adminEmail: string
    createdAt: string
    ring: string
    isSetupComplete: boolean
}

export interface ListTenantsResponse {
    items: TenantSummary[]
    totalCount: number
    page: number
    pageSize: number
}

export interface TenantCompany {
    companyId: string
    displayName: string
    isDefault: boolean
    createdAt: string
    // AB#1753 — 4-char quick-reference code, mirrored from the tenant plane.
    companyCode: string | null
    // AB#1752 — detail set mirrored from the tenant plane (null until the tenant pushes it).
    addressLine1: string | null
    addressLine2: string | null
    city: string | null
    region: string | null
    postcode: string | null
    country: string | null
    phone: string | null
    email: string | null
    registrationNumber: string | null
    vatNumber: string | null
    baseCurrency: string | null
    fiscalYearStartMonth: number | null
    isSetupComplete: boolean
}

export interface TenantIdpConnection {
    connectionId: string
    connectionSlug: string
    displayName: string
    providerKey: string
    issuer: string
    clientId: string
    clientSecretRef: string
    redirectUri: string
    scopes: string[]
    allowedEmailDomains: string[]
    isPrimary: boolean
    enabled: boolean
    verificationToken: string | null
    domainVerifiedAt: string | null
    createdAt: string
    updatedAt: string
}

export interface TenantAuthPolicy {
    tenantId: string
    localUsersEnabled: boolean
}

export interface VerifyDomainResponse {
    verified: boolean
    failureReason: string | null
}

export interface TenantDetail {
    tenantId: string
    slug: string
    displayName: string
    provisioningMode: string
    status: string
    adminEmail: string
    ring: string
    isSetupComplete: boolean
    // AB#2796 — company-count limit (replaces AB#1626's boolean flag). Default 5.
    maxCompanies: number
    // AB#2812 — the stored pricing data-source name; null = the platform default
    // (marque-entity-records), resolved when the entitlement snapshot is served.
    pricingDataSource: string | null
    // AB#2932 — the stored bootstrap role code; null = tenant-owner semantics.
    bootstrapRoleCode: string | null
    // AB#2457 — operator-managed platform Google key grants.
    usePlatformMapsKey: boolean
    usePlatformPlacesKey: boolean
    // AB#2933 — operator-managed platform mailbox grant.
    usePlatformMailbox: boolean
    // AB#3770 — operator-managed platform Document Intelligence grant (handwriting transcription); defaults to true.
    usePlatformDocumentIntelligence: boolean
    createdAt: string
    updatedAt: string
    // AB#1628 — set when the tenant is archived (soft-deleted); null otherwise.
    // Drives the resurrect window countdown in the Danger zone.
    archivedAt: string | null
    companies: TenantCompany[]
    idpConnections: TenantIdpConnection[]
}

export interface ProvisionTenantRequest {
    slug: string | null
    displayName: string
    provisioningMode: 'Shared' | 'Dedicated'
    adminEmail: string
    defaultCompanyName: string
    localUsersEnabled: boolean
    // AB#3007 — which PRODUCT this tenant is. 'Erp' (the default, and what every caller that omits
    // it gets) auto-grants the core-default modules; 'NonErp' grants only the modules named below or
    // by a standalone bundle, and withholds each module's declared full-ERP backend default.
    kind?: 'Erp' | 'NonErp'
    // AB#3007 — an explicit module set, dependency-expanded server-side. Mutually exclusive with
    // bundleKey: they are two answers to the same question and there is no correct way to merge them.
    moduleIds?: string[] | null
    // AB#1297/1298 — optional commercial bundle; entitles the bundle's effective module set.
    bundleKey?: string | null
    // AB#2799 — optional first-admin role code (null = tenant-owner; pricing-service tenants use pricing-admin).
    bootstrapRoleCode?: string | null
    // AB#2931 — optional module id → chosen backend, for modules whose catalogue entry declares
    // backends. Absent/unnamed modules run on their declared default.
    moduleBackends?: Record<string, string> | null
    // AB#1737 — optional default-company contact + address captured at provisioning.
    companyPhone?: string | null
    companyAddressLine1?: string | null
    companyAddressLine2?: string | null
    companyCity?: string | null
    companyRegion?: string | null
    companyPostcode?: string | null
    companyCountry?: string | null
}

export interface ProvisionTenantResponse {
    tenantId: string
    slug: string
    defaultCompanyId: string
}

export interface CreateIdpConnectionRequest {
    connectionSlug: string | null
    displayName: string
    providerKey: 'entraid'
    issuer: string
    clientId: string
    clientSecret: string
    redirectUri: string
    scopes: string[]
    allowedEmailDomains: string[]
    isPrimary: boolean
}

export interface UpdateIdpConnectionRequest {
    displayName: string
    issuer: string
    clientId: string
    clientSecret?: string
    redirectUri: string
    scopes: string[]
    allowedEmailDomains: string[]
    isPrimary: boolean
    enabled: boolean
}

export interface UpdateIdpConnectionResponse {
    tenantId: string
    connectionId: string
    connectionSlug: string
}

export interface ModuleCatalogItem {
    moduleId: string
    name: string
    description: string
    isCoreDefault: boolean
    sortOrder: number
    // AB#2931 — the module's declared pluggable backends (null = not pluggable) and which one
    // applies inside a full ERP tenant. Render the choice from these, never a hard-coded list.
    backends?: string[] | null
    defaultBackend?: string | null
    // AB#3007 — may this module be offered to a non-ERP tenant, and must a backend be chosen for it
    // explicitly. Orthogonal to isCoreDefault: pricing-core is both core-default and standalone.
    isStandalone?: boolean
    backendRequired?: boolean
}

// AB#1296/1298 — commercial module bundles (Basic/Professional/Enterprise).
export interface ModuleBundle {
    key: string
    name: string
    description: string
    sortOrder: number
    isActive: boolean
    declaredModules: string[]
    effectiveModules: string[]
    addedDependencies: string[]
    missingDependencies: string[]
    // AB#2282 — modules pulled in solely by the core-cohesion rule (any core module forces the whole core set in).
    addedByCore: string[]
    // AB#2799 — a standalone product bundle uses the plain dependsOn closure (no core-set forcing).
    standalone: boolean
    // AB#2806 — bundle-declared first-admin role: provisioning stamps it on the tenant when the
    // command carries no explicit bootstrapRoleCode (an explicit value always wins). Null = none.
    bootstrapRoleCode: string | null
    // AB#2812 — bundle-declared pricing data source, stamped at provisioning with the identical
    // precedence (explicit command value always wins). Null = no declaration.
    pricingDataSource: string | null
}

export interface CreateModuleBundleRequest {
    key: string
    name: string
    description?: string
    sortOrder?: number
    isActive?: boolean
    // AB#2799 — standalone product bundle (plain dependency closure, no AB#2282 core cohesion).
    standalone?: boolean
    // AB#2806 — optional bundle-declared first-admin role (lower-kebab-case when present).
    bootstrapRoleCode?: string | null
    // AB#2812 — optional bundle-declared pricing data source (lower-kebab-case when present).
    pricingDataSource?: string | null
}

export interface UpdateModuleBundleRequest {
    name: string
    description?: string
    sortOrder: number
    isActive: boolean
    standalone?: boolean
    // AB#2806 — optional bundle-declared first-admin role; omitting it clears the declaration.
    bootstrapRoleCode?: string | null
    // AB#2812 — optional bundle-declared pricing data source; omitting it clears the declaration.
    pricingDataSource?: string | null
}

export interface ModuleEntitlement {
    moduleId: string
    entitledFrom: string
    entitledUntil: string | null
    isTrial: boolean
    source: string
    // AB#2494 — the module's currently PUBLISHED version, or null when the tenant is entitled to a module
    // that has never been published. Those are different problems, so null is meaningful and must not be
    // rendered as a version. Platform-wide, not per tenant: modules are published centrally and a tenant is
    // entitled to a module, never pinned to a version of it.
    publishedVersion: string | null
    // AB#2931 — the entitlement's chosen backend; null = the module's declared default (or the
    // module is not pluggable — the catalogue's backends declaration distinguishes the two).
    backend?: string | null
    // AB#2953 — what is ACTUALLY in force, and where it came from. A null `backend` does NOT mean
    // the declared default applies: the legacy tenant pricing-data-source scalar outranks it for
    // the pricing module. Render THIS, never an inference from `backend` being null.
    effectiveBackend?: string | null
    backendSource?: 'entitlementRow' | 'legacyPricingDataSource' | 'declaredDefault' | 'none'
}

export interface GrantModuleRequest {
    isTrial: boolean
    effectiveFrom: string | null
    entitledUntil: string | null
    notes: string | null
    // AB#2931 — chosen backend for a module whose catalogue entry declares any (null = declared default).
    backend?: string | null
}

export interface GrantModuleResponse {
    tenantId: string
    moduleId: string
    entitledFrom: string
    entitledUntil: string | null
    isTrial: boolean
    wasAlreadyActive: boolean
    // AB#2930 — modules the grant pulled in via the dependency closure (absent/empty = none).
    cascadedDependencies?: string[] | null
    // AB#2953 — the already-active call switched the module's backend rather than doing nothing.
    backendChanged?: boolean
}

export interface RevokeModuleResponse {
    tenantId: string
    moduleId: string
    revokedAt: string
    wasAlreadyInactive: boolean
}

export interface BootstrapTenantResponse {
    tenantId: string
    adminEmail: string
    expiresAt: string
    bootstrapToken: string
}

export interface CompleteSetupResponse {
    tenantId: string
    slug: string
    isSetupComplete: boolean
}

// AB#3066 — operator invites a user to a tenant; inviteUrl is always returned so the
// operator can hand the link over when email is unconfigured or the send failed.
export interface InviteTenantUserResponse {
    tenantId: string
    email: string
    inviteUrl: string
    expiresAt: string
    emailSent: boolean
    emailConfigured: boolean
    emailError: string | null
}

// AB#1468 — the V1 ERP demo seeder + clear-company-data have been retired; their DTOs are removed.

export interface PermissionDenialItem {
    id: string
    userId: string | null
    userEmail: string
    permissionCode: string
    requestPath: string
    requestMethod: string
    deniedAt: string
}

export interface PermissionDenialsResponse {
    items: PermissionDenialItem[]
    totalCount: number
    page: number
    pageSize: number
}

export interface RoleTemplateItem {
    roleCode: string
    name: string
    patterns: string[]
    updatedAt: string
}

export interface RoleTemplateListResponse {
    items: RoleTemplateItem[]
}

export interface RoleTemplateSyncStatus {
    slug: string
    isSynced: boolean
    lastPushedAt: string | null
    templatesUpdatedAt: string | null
}

export interface PushRoleTemplatesResponse {
    slug: string
    rowsAffected: number
    mode: string
    pushedAt: string
}

export interface PushAllRoleTemplatesResponse {
    results: Array<{
        slug: string
        rowsAffected: number
        mode: string
        pushedAt: string
        error: string | null
    }>
}

export interface SqlQueryRequest {
    query: string
}

export interface SqlQueryResponse {
    columns: string[]
    rows: (string | null)[][]
    rowCount: number
    durationMs: number
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(path, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...(init?.headers ?? {}),
        },
    })
    if (!res.ok) {
        let message = `HTTP ${res.status} ${res.statusText}`
        let fieldErrors: Record<string, string[]> = {}
        try {
            const body = await res.json() as { title?: string; detail?: string; errors?: Record<string, string[]> }
            if (body?.title) message = body.title
            // AB#3066 — problem responses put the actionable guidance in `detail` (the title is a
            // short label); prefer it so refusal reasons actually reach the operator. Matches the
            // tenant-plane client's behaviour.
            if (body?.detail) message = body.detail
            if (body?.errors) fieldErrors = body.errors
        } catch { /* ignore parse errors — use the default message */ }
        throw new ApiError(res.status, message, fieldErrors)
    }
    const text = await res.text()
    return (text ? JSON.parse(text) : undefined) as T
}

export interface AssistantPolicy {
    enabled: boolean
    revision: number
    allowCentralProvider: boolean
    requireTenantProvider: boolean
    centralProfileId: string | null
    monthlyTokenLimit: number
}

export const centralApi = {
    getAssistantProfiles: () => apiFetch<import('../tenant/assistant').RalphProfile[]>('/api/config/assistant-profiles'),
    saveAssistantProfile: (profile: { id: string; name: string; revision: number; enabled: boolean; definition: { adapter: string; model: string; endpoint: string; maxOutputTokens: number } }, secret: string | null) =>
        apiFetch(`/api/config/assistant-profiles/${profile.id}`, { method: 'PUT', body: JSON.stringify({ ...profile, expectedRevision: profile.revision, secret }) }),
    getAssistantPolicy: (slug: string) => apiFetch<AssistantPolicy>(`/api/tenants/${encodeURIComponent(slug)}/assistant-policy`),
    setAssistantPolicy: (slug: string, policy: AssistantPolicy) =>
        apiFetch<AssistantPolicy>(`/api/tenants/${encodeURIComponent(slug)}/assistant-policy`, {
            method: 'PUT', body: JSON.stringify({ ...policy, expectedRevision: policy.revision }),
        }),
    previewStandaloneCorrection: (slug: string) => apiFetch<{
        kind: string; updatedAt: string; blockers: string[];
        modules: { moduleId: string; beforeBackend: string | null; afterBackend: string | null; provisioningEvidence: string | null }[]
    }>(`/api/tenants/${slug}/standalone-correction/`),
    correctStandaloneKind: (slug: string, expectedUpdatedAt: string, reason: string) =>
        apiFetch<{ kind: string }>(`/api/tenants/${slug}/standalone-correction/`, {
            method: 'POST', body: JSON.stringify({ expectedUpdatedAt, reason }),
        }),
    getMe: () => apiFetch<OperatorSession>('/bff/me'),

    listTenants: (page = 1, pageSize = 20) =>
        apiFetch<ListTenantsResponse>(`/api/tenants?page=${page}&pageSize=${pageSize}`),

    listAllTenants: () =>
        apiFetch<ListTenantsResponse>('/api/tenants?page=1&pageSize=1000'),

    getTenant: (slug: string) =>
        apiFetch<TenantDetail>(`/api/tenants/${slug}`),

    provisionTenant: (req: ProvisionTenantRequest) =>
        apiFetch<ProvisionTenantResponse>('/api/tenants', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    changeTenantStatus: (slug: string, status: string) =>
        apiFetch<{ slug: string; status: string }>(`/api/tenants/${slug}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        }),

    // AB#1628 — archive (soft-delete) starts the 30-day retention timer and
    // blocks sign-in; resurrect (unarchive) returns the tenant to Active within
    // that window. Both ride the status endpoint.
    archiveTenant: (slug: string) =>
        apiFetch<{ slug: string; status: string }>(`/api/tenants/${slug}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'Archived' }),
        }),

    resurrectTenant: (slug: string) =>
        apiFetch<{ slug: string; status: string }>(`/api/tenants/${slug}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'Active' }),
        }),

    createIdpConnection: (slug: string, req: CreateIdpConnectionRequest) =>
        apiFetch<TenantIdpConnection>(`/api/tenants/${slug}/idp-connections`, {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    updateIdpConnection: (
        slug: string,
        connectionId: string,
        req: UpdateIdpConnectionRequest,
    ) =>
        apiFetch<UpdateIdpConnectionResponse>(
            `/api/tenants/${slug}/idp-connections/${connectionId}`,
            { method: 'PATCH', body: JSON.stringify(req) },
        ),

    deleteIdpConnection: (slug: string, connectionId: string) =>
        apiFetch<void>(`/api/tenants/${slug}/idp-connections/${connectionId}`, {
            method: 'DELETE',
        }),

    listModuleCatalog: () =>
        apiFetch<ModuleCatalogItem[]>('/api/catalog/modules'),

    // AB#2796 — module entitlement is tenant-scoped; the companies/{companyId} segment is gone.
    listModules: (slug: string) =>
        apiFetch<ModuleEntitlement[]>(
            `/api/tenants/${slug}/modules`,
        ),

    grantModule: (slug: string, moduleId: string, req: GrantModuleRequest) =>
        apiFetch<GrantModuleResponse>(
            `/api/tenants/${slug}/modules/${encodeURIComponent(moduleId)}`,
            { method: 'PUT', body: JSON.stringify(req) },
        ),

    revokeModule: (slug: string, moduleId: string) =>
        apiFetch<RevokeModuleResponse>(
            `/api/tenants/${slug}/modules/${encodeURIComponent(moduleId)}`,
            { method: 'DELETE', body: JSON.stringify({ revokedAt: null, reason: null }) },
        ),

    bootstrapTenant: (slug: string, adminEmail: string) =>
        apiFetch<BootstrapTenantResponse>(`/api/tenants/${slug}/bootstrap`, {
            method: 'POST',
            body: JSON.stringify({ adminEmail }),
        }),

    completeSetup: (slug: string) =>
        apiFetch<CompleteSetupResponse>(`/api/tenants/${slug}/complete-setup`, {
            method: 'POST',
        }),

    // AB#2457 — platform Google key grants: operator-controlled, never by the tenant.
    // AB#2933 — the platform mailbox grant rides the same PATCH; omitting it leaves it unchanged.
    // AB#3770 — the platform Document Intelligence grant rides it too; omitting it leaves it unchanged
    // (it defaults to ON, so an older caller must not be able to revoke it by accident).
    setTenantPlatformKeys: (slug: string, useMapsKey: boolean, usePlacesKey: boolean, useMailbox?: boolean, useDocumentIntelligence?: boolean) =>
        apiFetch<{ tenantId: string; slug: string; usePlatformMapsKey: boolean; usePlatformPlacesKey: boolean; usePlatformMailbox: boolean; usePlatformDocumentIntelligence: boolean }>(
            `/api/tenants/${encodeURIComponent(slug)}/platform-keys`,
            { method: 'PATCH', body: JSON.stringify({ useMapsKey, usePlacesKey, useMailbox, useDocumentIntelligence }) }),

    // AB#2796 — set the tenant's company-count limit (replaces AB#1626's boolean toggle).
    setMaxCompanies: (slug: string, maxCompanies: number) =>
        apiFetch<{ tenantId: string; slug: string; maxCompanies: number }>(
            `/api/tenants/${slug}/max-companies`, {
                method: 'PATCH',
                body: JSON.stringify({ maxCompanies }),
            }),

    // AB#2812 — set the tenant's pricing data-source name (null clears back to the default).
    // AB#2932 — set the tenant's bootstrap role code (null clears to tenant-owner semantics).
    setBootstrapRole: (slug: string, bootstrapRoleCode: string | null) =>
        apiFetch<{ tenantId: string; slug: string; bootstrapRoleCode: string | null }>(
            `/api/tenants/${slug}/bootstrap-role`, {
                method: 'PATCH',
                body: JSON.stringify({ bootstrapRoleCode }),
            }),

    setPricingDataSource: (slug: string, pricingDataSource: string | null) =>
        apiFetch<{ tenantId: string; slug: string; pricingDataSource: string | null }>(
            `/api/tenants/${slug}/pricing-data-source`, {
                method: 'PATCH',
                body: JSON.stringify({ pricingDataSource }),
            }),

    resendWelcomeEmail: (slug: string) =>
        apiFetch<void>(`/api/tenants/${slug}/resend-welcome-email`, { method: 'POST' }),

    // AB#3066 — invite a user to an Active tenant (same accept flow as tenant-admin invites).
    inviteUser: (slug: string, email: string) =>
        apiFetch<InviteTenantUserResponse>(`/api/tenants/${slug}/invitations`, {
            method: 'POST',
            body: JSON.stringify({ email }),
        }),

    // AB#1468 — seedDemoDataStream + clearCompanyData removed (V1 ERP demo retired).

    executeSql: (slug: string, query: string) =>
        apiFetch<SqlQueryResponse>(`/api/tenants/${slug}/sql`, {
            method: 'POST',
            body: JSON.stringify({ query } satisfies SqlQueryRequest),
        }),

    getAuthPolicy: (slug: string) =>
        apiFetch<TenantAuthPolicy>(`/api/tenants/${slug}/auth-policy`),

    updateAuthPolicy: (slug: string, localUsersEnabled: boolean) =>
        apiFetch<TenantAuthPolicy>(`/api/tenants/${slug}/auth-policy`, {
            method: 'PATCH',
            body: JSON.stringify({ localUsersEnabled }),
        }),

    getTenantPermissionDenials: (slug: string, page = 1, pageSize = 50) =>
        apiFetch<PermissionDenialsResponse>(
            `/api/tenants/${slug}/permission-denials?page=${page}&pageSize=${pageSize}`,
        ),

    getRoleTemplates: () =>
        apiFetch<RoleTemplateListResponse>('/api/role-templates'),

    updateRoleTemplate: (roleCode: string, patterns: string[]) =>
        apiFetch<RoleTemplateItem>(`/api/role-templates/${encodeURIComponent(roleCode)}`, {
            method: 'PUT',
            body: JSON.stringify({ patterns }),
        }),

    pushAllRoleTemplates: (mode: 'merge' | 'replace' = 'merge') =>
        apiFetch<PushAllRoleTemplatesResponse>('/api/role-templates/push-all', {
            method: 'POST',
            body: JSON.stringify({ mode }),
        }),

    pushRoleTemplatesToTenant: (slug: string, mode: 'merge' | 'replace' = 'merge') =>
        apiFetch<PushRoleTemplatesResponse>(`/api/tenants/${slug}/push-role-templates`, {
            method: 'POST',
            body: JSON.stringify({ mode }),
        }),

    getRoleTemplateSyncStatus: (slug: string) =>
        apiFetch<RoleTemplateSyncStatus>(`/api/tenants/${slug}/role-template-sync`),

    verifyIdpDomain: (slug: string, connectionId: string) =>
        apiFetch<VerifyDomainResponse>(
            `/api/tenants/${slug}/idp-connections/${connectionId}/verify-domain`,
            { method: 'POST' },
        ),

    // ---- Module bundles (AB#1296/1298) ----

    listBundles: () =>
        apiFetch<ModuleBundle[]>('/api/bundles'),

    getBundle: (key: string) =>
        apiFetch<ModuleBundle>(`/api/bundles/${encodeURIComponent(key)}`),

    createBundle: (req: CreateModuleBundleRequest) =>
        apiFetch<ModuleBundle>('/api/bundles', { method: 'POST', body: JSON.stringify(req) }),

    updateBundle: (key: string, req: UpdateModuleBundleRequest) =>
        apiFetch<ModuleBundle>(`/api/bundles/${encodeURIComponent(key)}`, {
            method: 'PUT',
            body: JSON.stringify(req),
        }),

    setBundleModules: (key: string, moduleIds: string[]) =>
        apiFetch<ModuleBundle>(`/api/bundles/${encodeURIComponent(key)}/modules`, {
            method: 'PUT',
            body: JSON.stringify({ moduleIds }),
        }),

    deleteBundle: (key: string) =>
        apiFetch<void>(`/api/bundles/${encodeURIComponent(key)}`, { method: 'DELETE' }),

    // ---- Platform SMTP config ----

    getSmtpConfig: () =>
        apiFetch<SmtpConfigResponse>('/api/config/smtp'),

    setSmtpConfig: (req: SetSmtpConfigRequest) =>
        apiFetch<void>('/api/config/smtp', { method: 'PUT', body: JSON.stringify(req) }),

    // ---- AB#1865 — Anthropic API key (central config secret) ----

    getAnthropicKeyConfigured: async (): Promise<boolean> => {
        try {
            const e = await apiFetch<ConfigEntryResponse>('/api/config/anthropic.api-key')
            return e.isSecret || (e.value != null && e.value !== '')
        } catch (err) {
            if (err instanceof ApiError && err.status === 404) { return false }
            throw err
        }
    },

    setAnthropicKey: (value: string) =>
        apiFetch<void>('/api/config/anthropic.api-key/secret', {
            method: 'PUT',
            body: JSON.stringify({ value, description: 'Anthropic API key' }),
        }),

    // ---- AB#2457 — platform Google keys (tenants opt in per API) ----

    getGoogleConfig: () =>
        apiFetch<GoogleConfigResponse>('/api/config/google'),

    // null = untouched, empty string = clear, non-empty = set (the write-only trio).
    setGoogleConfig: (req: SetGoogleConfigRequest) =>
        apiFetch<GoogleConfigResponse>('/api/config/google', { method: 'PUT', body: JSON.stringify(req) }),

    // ---- AB#3770 — platform Azure Document Intelligence (tenants granted by default) ----

    getDocumentIntelligenceConfig: () =>
        apiFetch<DocumentIntelligenceConfigResponse>('/api/config/document-intelligence'),

    // null = untouched, empty string = clear (back to the deployment seed), non-empty = set.
    setDocumentIntelligenceConfig: (req: SetDocumentIntelligenceConfigRequest) =>
        apiFetch<DocumentIntelligenceConfigResponse>('/api/config/document-intelligence', { method: 'PUT', body: JSON.stringify(req) }),

    // AB#2123 — per-module definition change feed. `id` is a module_packages.id (Guid). Optional filters
    // narrow by object type and source. Summaries only; the before/after JSON is fetched per revision by the
    // designer History tab, not this feed.
    getModuleDefinitionAudit: (id: string, filters?: { objectType?: string; source?: string }) => {
        const params = new URLSearchParams()
        if (filters?.objectType) { params.set('objectType', filters.objectType) }
        if (filters?.source) { params.set('source', filters.source) }
        const qs = params.toString()
        return apiFetch<DefinitionAuditRow[]>(
            `/api/modules/${encodeURIComponent(id)}/definition-audit${qs ? `?${qs}` : ''}`,
        )
    },

    // AB#2123 — installed module packages, so the Modules page can list them and drill into each one's
    // definition change feed. Mirrors GET /api/modules (ListModulePackages).
    listModulePackages: () =>
        apiFetch<ModulePackageSummary[]>('/api/modules'),

    // AB#2149 — module lifecycle (AB#2081 back-end). Deprecation is an advisory flag.
    deprecateModulePackage: (id: string) =>
        apiFetch<void>(`/api/modules/${encodeURIComponent(id)}/deprecate`, { method: 'POST' }),

    undeprecateModulePackage: (id: string) =>
        apiFetch<void>(`/api/modules/${encodeURIComponent(id)}/undeprecate`, { method: 'POST' }),

    // AB#2149 — hard uninstall. Non-throwing: the 409 carries the blocker detail (active entitlement
    // count, dependent module ids) the UI must render, which apiFetch's ApiError would discard.
    uninstallModulePackage: (id: string) =>
        moduleLifecycleFetch(`/api/modules/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    // AB#2149 — the destructive cascade: revokes every active entitlement, then uninstalls. The typed
    // module id is passed through verbatim — the server validates the echo.
    revokeAllAndUninstallModule: (id: string, confirmModuleId: string) =>
        moduleLifecycleFetch(`/api/modules/${encodeURIComponent(id)}/revoke-all-and-uninstall`, {
            method: 'POST',
            body: JSON.stringify({ confirmModuleId }),
        }),
}

// ---- AB#2149 — module lifecycle result shapes ----

export interface ModuleUninstallBlockers {
    moduleId: string
    activeEntitledCompanies: number
    dependentModuleIds: string[]
}

export type ModuleLifecycleResult =
    | { ok: true; moduleId: string; revokedEntitlements?: number | undefined; removed: Record<string, number | boolean> }
    | { ok: false; status: number; message: string; blockers: ModuleUninstallBlockers | null }

// The uninstall verbs return rich bodies on BOTH outcomes (removed-counts on 200, problem+json with
// blocker extensions on 409, {error} on a 400 confirmation mismatch) — parse instead of throwing.
async function moduleLifecycleFetch(path: string, init: RequestInit): Promise<ModuleLifecycleResult> {
    const res = await fetch(path, {
        ...init,
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
    let body: Record<string, unknown> | null = null
    try { body = await res.json() as Record<string, unknown> } catch { /* non-JSON error page */ }

    if (res.ok) {
        return {
            ok: true,
            moduleId: String(body?.moduleId ?? ''),
            revokedEntitlements: typeof body?.revokedEntitlements === 'number' ? body.revokedEntitlements : undefined,
            removed: (body?.removed ?? {}) as Record<string, number | boolean>,
        }
    }

    const blockers = typeof body?.activeEntitledCompanies === 'number'
        ? {
            moduleId: String(body.moduleId ?? ''),
            activeEntitledCompanies: body.activeEntitledCompanies,
            dependentModuleIds: Array.isArray(body.dependentModuleIds) ? body.dependentModuleIds as string[] : [],
        }
        : null
    const message = typeof body?.detail === 'string' ? body.detail
        : typeof body?.error === 'string' ? body.error
        : typeof body?.title === 'string' ? body.title
        : `HTTP ${res.status}`
    // AB#2204 — an UNHANDLED server fault deliberately carries no detail: MarqueExceptionHandler keeps the
    // exception (SQL text, stack frames) server-side and sends only a fixed title plus a traceId. That
    // left the operator with a bare "An unexpected error occurred." and nothing to quote — a real
    // uninstall failure took a log dig to diagnose. Surface the traceId, which is safe by construction
    // and is exactly what correlates with the server log entry. Handled refusals (400/409) carry a real
    // detail and no traceId, so they are unaffected.
    const traceId = typeof body?.traceId === 'string' ? body.traceId : null
    return {
        ok: false,
        status: res.status,
        message: traceId && !message.includes(traceId) ? `${message} (trace ${traceId})` : message,
        blockers,
    }
}

export interface ModulePackageSummary {
    id: string
    moduleId: string
    /** Packed monotonic version counter (e.g. 30200) — ordering only; display semver instead. */
    version: number
    /** AB#2148 — human-readable X.Y.Z from the package metadata. */
    semver: string
    displayName: string
    installedAt: string
    deprecated: boolean
}

// ---- AB#2123 — definition audit change feed ----

export interface DefinitionAuditRow {
    id: string
    objectType: string
    objectKey: string
    author: string | null
    source: string
    createdAt: string
}

// ---- AB#1865 — generic config entry (secrets return value=null) ----

export interface ConfigEntryResponse {
    key: string
    value: string | null
    isSecret: boolean
    description: string | null
    updatedAt: string
}

// ---- SMTP config DTOs ----

export interface SmtpConfigResponse {
    isConfigured: boolean
    host: string | null
    port: number | null
    sslMode: string | null
    fromAddress: string | null
    fromName: string | null
    hasUsername: boolean
    authMode: string | null
    oauth2TenantId: string | null
    oauth2ClientId: string | null
    hasOAuth2ClientSecret: boolean
}

export interface SetSmtpConfigRequest {
    host: string | null
    port: number | null
    sslMode: string | null
    username: string | null
    password: string | null
    fromAddress: string | null
    fromName: string | null
    authMode: string | null
    oauth2TenantId: string | null
    oauth2ClientId: string | null
    oauth2ClientSecret: string | null
}

// AB#2457 — platform Google keys. The maps CLIENT key reads back (browser-delivered, referrer-
// restricted); the places SERVER key is write-only — only its has-flag ever returns.
export interface GoogleConfigResponse {
    mapsClientKey: string | null
    hasPlacesServerKey: boolean
}

export interface SetGoogleConfigRequest {
    mapsClientKey?: string | null
    placesServerKey?: string | null
}

// AB#3770 — platform Azure Document Intelligence. The endpoint reads back; the key is write-only
// (has-flag only). Each value says where it came from: an operator entry, the deployment seed
// (Terraform -> Key Vault -> central-api configuration), or nowhere.
export type PlatformConfigSource = 'operator' | 'deployment' | 'none'

export interface DocumentIntelligenceConfigResponse {
    endpoint: string | null
    endpointSource: PlatformConfigSource
    hasApiKey: boolean
    apiKeySource: PlatformConfigSource
}

export interface SetDocumentIntelligenceConfigRequest {
    endpoint?: string | null
    apiKey?: string | null
}
